import Cal, { getCalApi } from "@calcom/embed-react";
import { Alert } from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import ErrorBoundary from "~/components/ErrorBoundary";
import type { MentorshipSession } from "~/models/Mentor";
import MentorshipAPI from "~/services/MentorshipAPI";
import Utils from "~/services/Utils";

interface ScheduleStepProps {
  scheduleURL?: string | null;
  mentorshipSession?: MentorshipSession | null;
  onSessionScheduled: (session: MentorshipSession) => void;
}

export default function ScheduleStep(props: ScheduleStepProps) {
  console.log("ScheduleStep props", props);
  const CALCOM_URL = "https://cal.com/";
  const { scheduleURL, mentorshipSession, onSessionScheduled } = props;

  const [networkResponse, setNetworkResponse] = useState<any>(null);
  const [calError, setCalError] = useState<string | null>(null);
  const mentorshipSessionRef = useRef(mentorshipSession);
  
  // Update ref when mentorshipSession changes
  useEffect(() => {
    mentorshipSessionRef.current = mentorshipSession;
  }, [mentorshipSession]);

  useEffect(() => {
    let isMounted = true;
    
    (async function() {
      try {
        const cal = await getCalApi({"namespace": "atila-mentorship-30"});
        if (isMounted) {
          cal("ui", {"hideEventTypeDetails": false, "layout": "month_view"});
        }
      } catch (error) {
        console.error("Error initializing Cal API:", error);
        if (isMounted) {
          setCalError("Failed to initialize calendar. Please refresh the page.");
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCalendarEventScheduled = useCallback((session: MentorshipSession, eventDetails: any) => {
    setNetworkResponse({title: "Saving event details.", type: "loading"});
    MentorshipAPI.sessionScheduled(session.id!, eventDetails)
      .then(res => {
        const { data } = res;
        console.log({data});
        onSessionScheduled(data.session);
        setNetworkResponse({title: "Successfully scheduled event", type: "success"});
      })
      .catch(error => {
        console.log({error});
        setNetworkResponse({title: Utils.getErrorMessage(error), type: "error"});
      });
  }, []);

  const isCalComEvent = useCallback((e: MessageEvent) => {
    return e.origin === 'https://app.cal.com' && !['__routeChanged', '__dimensionChanged'].includes(e.data.type);
  }, []);

  const handleCalendarEvent = useCallback((event: MessageEvent) => {
    console.log("handleCalendarEvent() Received message from iframe:", event);
    
    try {
      if (event.data && event.data.type === "CALENDAR_EVENT") {
        console.log("Calendar event received:", event.data);
      } else {
        console.log("Received non-calendar event:", event.data);
      }
      
      if (isCalComEvent(event) && mentorshipSessionRef.current) {
        console.log(event.data.type);
        console.log({event});
        if (event.data.type === 'bookingSuccessful') {
          // Handle the booking successful event
          // handleCalendarEventScheduled(mentorshipSessionRef.current, event.data.data);
        }
      }
    } catch (error) {
      console.error("Error handling calendar event:", error);
    }
  }, [isCalComEvent, handleCalendarEventScheduled]);

  useEffect(() => {
    window.addEventListener('message', handleCalendarEvent);
  
    return () => {
      window.removeEventListener('message', handleCalendarEvent);
    };
  }, [handleCalendarEvent]); // Use the memoized callback

  const renderCalendar = () => {
    if (calError) {
      return (
        <Alert
          message="Calendar Error"
          description={calError}
          type="error"
          showIcon
        />
      );
    }

    if (!scheduleURL?.includes(CALCOM_URL)) {
      return (
        <Alert
          message="Invalid Schedule URL"
          description="Please provide a valid Cal.com scheduling URL."
          type="warning"
          showIcon
        />
      );
    }

    return (
      <ErrorBoundary
        fallback={
          <Alert
            message="Calendar Loading Error"
            description="There was an error loading the calendar. Please refresh the page or contact support."
            type="error"
            showIcon
          />
        }
      >
        <Cal 
          namespace="atila-mentorship-30"
          calLink={scheduleURL.replace(CALCOM_URL, '')}
          style={{width:"100%", height:"100%", overflow:"scroll"}}
          config={{"layout":"month_view"}}
        />
      </ErrorBoundary>
    );
  };

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-semibold">Schedule a Session</h4>
      
      {renderCalendar()}
      
      {networkResponse && (
        <Alert
          message={networkResponse.title}
          type={networkResponse.type}
          showIcon
          closable
          onClose={() => setNetworkResponse(null)}
        />
      )}
    </div>
  );
}